/**
 * Notification domain event schemas.
 *
 * Typed payload validation for all notification-related Mycelium events.
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module notification.events
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

// ── Event schemas ───────────────────────────────────────────────────────

export const EscalationStoppedEvent = BackboneEventSchema.extend({
  domain: z.literal('notification'),
  type: z.literal('escalation_stopped'),
  payload: z.object({
    notificationId: z.string(),
    stoppedAtLevel: z.number().optional(),
    acknowledgedChannel: z.string().optional(),
    source: z.string().optional(),
  }),
})

export const InvoiceOverdueEscalationEvent = BackboneEventSchema.extend({
  domain: z.literal('notification'),
  type: z.literal('invoice_overdue_escalation'),
  payload: z.object({
    invoiceId: z.string(),
    amount: z.number(),
    clientId: z.string(),
    daysOverdue: z.number(),
    source: z.string(),
  }),
})

export const SuggestInvoiceGenerationEvent = BackboneEventSchema.extend({
  domain: z.literal('notification'),
  type: z.literal('suggest_invoice_generation'),
  payload: z.object({
    clientId: z.string().optional(),
    reason: z.string().optional(),
    source: z.string(),
  }),
})

export const WorkflowFailureAlertEvent = BackboneEventSchema.extend({
  domain: z.literal('notification'),
  type: z.literal('workflow_failure_alert'),
  payload: z.object({
    workflowId: z.string(),
    workflowName: z.string(),
    executionId: z.string().optional(),
    errorMessage: z.string().optional(),
    severity: z.string().optional(),
    failureRate: z.number().optional(),
    consecutiveFailures: z.number().optional(),
    source: z.string(),
  }),
})

const NotificationSendPayload = z.object({
  notificationId: z.string(),
  channel: z.string(),
  userId: z.string().optional(),
  priority: z.number().optional(),
  escalationLevel: z.number().optional(),
  message: z.record(z.unknown()).optional(),
  sourceEventId: z.string().optional(),
  source: z.string().optional(),
})

export const NotificationDeliveryRequestedEvent = BackboneEventSchema.extend({
  domain: z.literal('notification'),
  type: z.literal('notification_delivery_requested'),
  payload: z.object({
    notificationId: z.string(),
    userId: z.string(),
    escalationLevel: z.number().optional(),
    source: z.string(),
  }),
})

export const NotificationSendPushEvent = BackboneEventSchema.extend({
  domain: z.literal('notification'),
  type: z.literal('notification_send_push'),
  payload: NotificationSendPayload,
})

export const NotificationSendDiscordEvent = BackboneEventSchema.extend({
  domain: z.literal('notification'),
  type: z.literal('notification_send_discord'),
  payload: NotificationSendPayload,
})

export const NotificationSendWhatsappEvent = BackboneEventSchema.extend({
  domain: z.literal('notification'),
  type: z.literal('notification_send_whatsapp'),
  payload: NotificationSendPayload,
})

export const NotificationSendTelegramEvent = BackboneEventSchema.extend({
  domain: z.literal('notification'),
  type: z.literal('notification_send_telegram'),
  payload: NotificationSendPayload,
})

export const NotificationSendSlackEvent = BackboneEventSchema.extend({
  domain: z.literal('notification'),
  type: z.literal('notification_send_slack'),
  payload: NotificationSendPayload,
})

export const NotificationSendEmailEvent = BackboneEventSchema.extend({
  domain: z.literal('notification'),
  type: z.literal('notification_send_email'),
  payload: NotificationSendPayload,
})

export const NotificationSendSmsEvent = BackboneEventSchema.extend({
  domain: z.literal('notification'),
  type: z.literal('notification_send_sms'),
  payload: NotificationSendPayload,
})

// ── All notification events ─────────────────────────────────────────────

export const NotificationDomainEvents = [
  EscalationStoppedEvent,
  InvoiceOverdueEscalationEvent,
  SuggestInvoiceGenerationEvent,
  WorkflowFailureAlertEvent,
  NotificationDeliveryRequestedEvent,
  NotificationSendPushEvent,
  NotificationSendDiscordEvent,
  NotificationSendWhatsappEvent,
  NotificationSendTelegramEvent,
  NotificationSendSlackEvent,
  NotificationSendEmailEvent,
  NotificationSendSmsEvent,
] as const
